// Accepting an invitation (F-IAM-08, spec/07 §6.5; ADR-0046): a public route, `/invitations/accept`. The
// link is `/invitations/accept#<token>`; the token is read from the fragment and kept in this browser
// (`invitation-token.ts`), so it never reaches a server log and survives creating an account in between.
//
// **Before signing in**, the page reads what the link is (`POST /v1/invitations/preview`): who invited the
// person to what — a café to run as its owner, invited by the Platform's team, or a staff role — for which
// address or number, until when. A link that no longer works is read as its status alone, and said so.
// Then one path for each kind of invitation:
//
//   email   "Create your account" — sign-up with the invited address prefilled and locked, its code step
//           kept — or "Sign in", or Continue with Google from either; back here afterwards.
//   phone   the invited number, locked: a code to it signs the person in, or creates the account with the
//           terms agreed when the number has none — only while phone sign-in is on (`auth.phoneSignIn`, off
//           since 2026-10-08). Off, the page says so plainly and offers signing in with a password; whoever
//           invited them can invite them again by email.
//
// **Signed in**, Accept is `POST /v1/invitations/accept`, which still needs exactly the invited contact,
// verified. Signed in as someone else, the page says which address or number the invitation is for and
// offers to sign out and use that one. A role that needs a second factor sets one up right here and
// accepts straight after. On success the token is forgotten and the person lands in the organisation
// (`/o/{slug}`).
//
// **The token this browser keeps is forgotten at every refusal that is final for whoever is signed in** —
// already on the staff, used, withdrawn, expired, unknown, a shared counter device, someone else's
// invitation — and at every sign-out (`forgetInvitationOnSignOut`), the plain "Sign out" here included. Only
// "Sign out and use that one" keeps it: that is the invited person on their way in. A shared front-desk
// computer is never left sending the next person who signs in to somebody else's invitation.

import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { fetchMyOrganizations } from '../api/me';
import { PROBLEM, problemCode } from '../api/problems';
import { queryKeys, useSession } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { Loading } from '../shell/layouts';
import { AuthLayout } from './AuthLayout';
import { Failure } from './form-controls';
import { pendingInvitationOf, useInvitationPreview, type PendingInvitation } from './invitation-preview';
import { clearInvitationToken, readInvitationToken, rememberInvitationExpiry } from './invitation-token';
import { InvitedPhoneSignIn } from './InvitedPhoneSignIn';
import { useSignInMethods } from './sign-in-methods';
import { ContinueWithProviders } from './social';
import { TwoFactorSetup } from './TwoFactorSetup';

/** Back to this same link once signed in or signed up (`/sign-in`'s and `/sign-up`'s own `redirect`). */
const RETURN_HERE = { redirect: '/invitations/accept' } as const;

type Refusal =
  | { readonly kind: 'message'; readonly key: string; readonly text?: string; readonly final: boolean }
  | { readonly kind: 'mismatch' }
  | { readonly kind: 'second-factor' };

/**
 * What a refused accept says, and whether it is final for whoever is signed in — then the kept token is
 * forgotten. A second factor to set up is not (it is set up here, and accepted straight after), and nor is an
 * error that may not happen again (the network, the server). Someone else's invitation is final for this
 * person: "Sign out and use that one" keeps the token again for the one it is for.
 */
function refusalOf(error: unknown, otherwise: string): Refusal {
  switch (problemCode(error)) {
    case PROBLEM.notFound:
    case PROBLEM.invitationUnavailable:
      return { kind: 'message', key: 'invitations.accept.failure.unavailable', final: true };
    case PROBLEM.invitationContactMismatch:
      return { kind: 'mismatch' };
    case PROBLEM.alreadyAMember:
      return { kind: 'message', key: 'invitations.accept.failure.alreadyMember', final: true };
    case PROBLEM.secondFactorRequired:
      return { kind: 'second-factor' };
    case PROBLEM.sharedDevice:
      return { kind: 'message', key: 'invitations.accept.failure.sharedDevice', final: true };
    default:
      // Any other answer reads the shared copy for its code (a rate limit, a plan that lacks this, a server fault).
      return { kind: 'message', key: 'errors.generic', text: otherwise, final: false };
  }
}

/** Is this refusal the end of the invitation for whoever is signed in? Then the token is not kept. */
function isFinalRefusal(refusal: Refusal): boolean {
  return refusal.kind === 'mismatch' || (refusal.kind === 'message' && refusal.final);
}

/** Who invited whom to what, in a sentence. */
function inviteLine(t: (key: string, values?: Record<string, unknown>) => string, preview: PendingInvitation): string {
  if (preview.invitedBy === 'hq') return t('invitations.accept.invitedByHq', { organization: preview.organizationName });
  const role = t(`invitations.accept.role.${preview.role}`, { defaultValue: preview.role });
  return preview.inviterName === null
    ? t('invitations.accept.invitedByOrganization', { organization: preview.organizationName, role })
    : t('invitations.accept.invitedByMember', { name: preview.inviterName, organization: preview.organizationName, role });
}

export function InvitationAcceptPage() {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const format = useVenueFormatter();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const session = useSession();
  // The router's own location, not `window.location`: it is what a memory-history test renders, and
  // what a browser's `hash` becomes once TanStack Router has parsed it (already without the `#`).
  const routerHash = useLocation({ select: (location) => location.hash });
  const [token] = useState<string | undefined>(() => readInvitationToken(routerHash));
  const preview = useInvitationPreview(token);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [accepting, setAccepting] = useState(false);
  const methods = useSignInMethods();

  const invitation = useMemo(() => pendingInvitationOf(preview.data), [preview.data]);

  // Keep the token exactly as long as the invitation lasts; forget one that can no longer be accepted.
  useEffect(() => {
    if (token === undefined) return;
    if (invitation !== undefined) rememberInvitationExpiry(token, invitation.expiresAt);
    else if (preview.data !== undefined || (preview.isError && problemCode(preview.error) === PROBLEM.notFound)) clearInvitationToken();
  }, [token, invitation, preview.data, preview.isError, preview.error]);

  const accept = async () => {
    if (token === undefined) return;
    setRefusal(null);
    setAccepting(true);
    try {
      const accepted = await api.acceptInvitation({ body: { token } });
      clearInvitationToken();
      await queryClient.invalidateQueries({ queryKey: queryKeys.myOrganizations });
      await queryClient.invalidateQueries({ queryKey: queryKeys.myVenues });
      const organizations = await queryClient.fetchQuery({
        queryKey: queryKeys.myOrganizations,
        queryFn: () => fetchMyOrganizations(api),
      });
      const organization = organizations.find((candidate) => candidate.id === accepted.organizationId);
      await navigate(organization === undefined ? { to: '/' } : { to: '/o/$orgSlug', params: { orgSlug: organization.slug } });
    } catch (error) {
      const refused = refusalOf(error, problem.text(error, { fallback: t('errors.generic') }));
      if (isFinalRefusal(refused)) clearInvitationToken();
      setRefusal(refused);
      setAccepting(false);
    }
  };

  const signedIn = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session });
    await queryClient.refetchQueries({ queryKey: queryKeys.session });
  };

  // As the shell's own sign-out does (`AppShell.tsx`): clear the cache, then leave — an in-place session
  // read does not reliably refetch to `null` in step. Every sign-out forgets the kept token; "Sign out and
  // use that one" (`here`) keeps it again, before it comes back here, for the person it is for. A plain
  // "Sign out" is someone leaving, and leaves nothing behind.
  const signOut = async (then: 'sign-in' | 'here') => {
    try {
      await api.signOut();
    } finally {
      queryClient.clear();
      setRefusal(null);
      if (then === 'here' && token !== undefined && invitation !== undefined) rememberInvitationExpiry(token, invitation.expiresAt);
      await navigate(then === 'sign-in' ? { to: '/sign-in' } : { to: '/invitations/accept' });
    }
  };

  const pending = invitation !== undefined;
  const forOwner = invitation?.invitedBy === 'hq';
  const expires = invitation === undefined ? '' : format.dateTime(invitation.expiresAt, { date: 'medium', time: 'short' });

  let body: ReactNode;
  if (token === undefined) {
    body = (
      <p className="console-alert" role="alert">
        {t('invitations.accept.missingToken')}
      </p>
    );
  } else if (preview.isPending || session.isPending || (session.data == null && methods.isPending)) {
    body = <Loading />;
  } else if (invitation === undefined) {
    // Unknown, used, withdrawn or expired: said once, the same way, with nothing to sign in for.
    body = (
      <p className="console-alert" role="alert">
        {preview.data !== undefined || problemCode(preview.error) === PROBLEM.notFound ? t('invitations.accept.failure.unavailable') : problem.text(preview.error, { fallback: t('errors.generic') })}
      </p>
    );
  } else if (session.data == null) {
    // Signed out, or the session read failed: the same safe answer either way.
    body = (
      <>
        <p className="console-invite__for">{t('invitations.accept.forContact', {
            // The preview names the invited contact in full to the link's holder (it prefills the sign-up), so the page does too
            // rather than masking here what it spells out a line below.
            destination: invitation.contact.email ?? invitation.contact.phoneNumber ?? invitation.destination,
            date: expires,
          })}</p>
        {invitation.channel === 'phone' && invitation.contact.phoneNumber !== undefined && methods.phone ? (
          <>
            <p className="console-muted">{t('invitations.accept.phoneIntro')}</p>
            <InvitedPhoneSignIn phoneNumber={invitation.contact.phoneNumber} onSignedIn={signedIn} />
          </>
        ) : invitation.channel === 'phone' ? (
          // Phone sign-in is off on the Platform: no code can go to the number. A phone-first person who has added a
          // password can still sign in with it and accept; anyone else is invited again, by email.
          <>
            <p className="console-alert" role="status">
              {t('invitations.accept.phoneOff')}
            </p>
            <div className="console-form__actions console-invite__actions">
              <Button asChild variant="primary">
                <Link to="/sign-in" search={RETURN_HERE}>
                  {t('invitations.accept.signIn')}
                </Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="console-muted">{t('invitations.accept.emailIntro', { email: invitation.contact.email ?? invitation.destination })}</p>
            <div className="console-form__actions console-invite__actions">
              <Button asChild variant="primary">
                <Link to="/sign-up" search={RETURN_HERE}>
                  {t('invitations.accept.createAccount')}
                </Link>
              </Button>
              <Button asChild variant="ghost">
                <Link to="/sign-in" search={RETURN_HERE}>
                  {t('invitations.accept.signIn')}
                </Link>
              </Button>
            </div>
            <ContinueWithProviders landing="/invitations/accept" onError="/sign-up?redirect=%2Finvitations%2Faccept" />
          </>
        )}
      </>
    );
  } else {
    const { user } = session.data;
    const name = user.name.trim() !== '' ? user.name : user.email;
    body = (
      <>
        <p className="console-muted">{t('invitations.accept.confirmIntro', { name })}</p>
        {refusal?.kind === 'message' ? <Failure message={refusal.text ?? t(refusal.key)} /> : null}
        {refusal?.kind === 'mismatch' ? (
          <div className="console-alert" role="alert">
            <p>{t('invitations.accept.failure.mismatchFor', { destination: invitation.contact.email ?? invitation.contact.phoneNumber ?? invitation.destination })}</p>
            <div className="console-form__actions">
              <Button variant="primary" onClick={() => void signOut('here')}>
                {t('invitations.accept.useThatOne')}
              </Button>
            </div>
          </div>
        ) : null}
        {refusal?.kind === 'second-factor' ? (
          <section className="console-invite__second-factor" aria-label={t('invitations.accept.secondFactorLabel')}>
            <p>{forOwner ? t('invitations.accept.secondFactorOwner') : t('invitations.accept.secondFactorRole')}</p>
            <TwoFactorSetup
              headingLevel={2}
              onVerified={async () => {
                // The session was stepped up: read it again, and accept straight away.
                await signedIn();
                await accept();
              }}
            />
          </section>
        ) : refusal?.kind === 'mismatch' ? null : (
          <div className="console-form__actions">
            <Button variant="primary" busy={accepting} onClick={() => void accept()}>
              {t('invitations.accept.accept')}
            </Button>
            <Button variant="ghost" onClick={() => void signOut('sign-in')}>
              {t('shell.signOut')}
            </Button>
          </div>
        )}
      </>
    );
  }

  const subtitle = invitation !== undefined ? inviteLine(t, invitation) : t('invitations.accept.subtitle');
  return (
    <AuthLayout
      title={forOwner && pending ? t('invitations.accept.titleOwner') : t('invitations.accept.title')}
      subtitle={subtitle}
      footer={forOwner && pending ? t('invitations.accept.footerOwner') : t('invitations.accept.footer')}
    >
      {body}
    </AuthLayout>
  );
}
